import { fetchClient } from "@/api/api";
import type {
	adminFcInternalSenderReloginInput,
	adminFcInternalSenderStopResumeInput,
	deleteAdminFcInternalSenderInput,
	getAdminCheckTMHistoryInput,
	getAdminCheckTMHistoryOutput,
	getAdminFcInternalSenderHistoryInput,
	getAdminFcInternalSenderHistoryOutput,
	getAdminFcInternalSenderInput,
	getAdminFcInternalSenderOutput,
	getAdminFcInternalSendersFacetsInput,
	getAdminFcInternalSendersFacetsOutput,
	getAdminFcInternalSendersInput,
	getAdminFcInternalSendersOutput,
} from "@/api/custom_types";
import useApiError from "@/hooks/useApiError";
import { createFacetSource } from "@/services/facets";
import type { ApiError } from "@/utils/fetching";
import type { CustomMutationHook, CustomQueryHook } from "@/utils/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ADMIN_FC_INTERNAL_SENDERS_SERVICE_KEYS } from "./keys";

export const useGetAdminFcInternalSenders: CustomQueryHook<
	getAdminFcInternalSendersOutput,
	getAdminFcInternalSendersInput
> = (payload, options) => {
	return useQuery({
		...options,
		queryKey:
			ADMIN_FC_INTERNAL_SENDERS_SERVICE_KEYS.useGetAdminFcInternalSenders(
				payload,
			),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/fc/admin/fc-senders/",
				{
					params: { query: payload },
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
	});
};

export const useGetAdminCheckTMHistory: CustomQueryHook<
	getAdminCheckTMHistoryOutput,
	getAdminCheckTMHistoryInput
> = (payload, options) => {
	return useQuery({
		...options,
		queryKey:
			ADMIN_FC_INTERNAL_SENDERS_SERVICE_KEYS.useGetAdminCheckTMHistory(payload),
		queryFn: async () => {
			const { fc_sender_id, ...query } = payload;
			const { data, error } = await fetchClient.GET(
				"/v1/fc/admin/fc-senders/{fc_sender_id}/check-tm-history/",
				{
					params: { path: { fc_sender_id }, query },
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
	});
};

export const useGetAdminFcInternalSendersFacets: CustomQueryHook<
	getAdminFcInternalSendersFacetsOutput,
	getAdminFcInternalSendersFacetsInput
> = (payload, options) => {
	return useQuery({
		...options,
		queryKey:
			ADMIN_FC_INTERNAL_SENDERS_SERVICE_KEYS.useGetAdminFcInternalSendersFacets(
				payload,
			),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/fc/admin/fc-senders/facets/",
				{
					params: { query: payload },
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
	});
};

export const INTERNAL_SENDERS_FACET_SOURCE =
	createFacetSource<getAdminFcInternalSendersFacetsOutput>({
		path: "/v1/fc/admin/fc-senders/facets/",
		queryKey: (query) =>
			ADMIN_FC_INTERNAL_SENDERS_SERVICE_KEYS.useGetAdminFcInternalSendersFacets(
				query as any,
			),
	});

export const useGetAdminFcInternalSender: CustomQueryHook<
	getAdminFcInternalSenderOutput,
	getAdminFcInternalSenderInput
> = (payload, options) => {
	return useQuery({
		...options,
		queryKey:
			ADMIN_FC_INTERNAL_SENDERS_SERVICE_KEYS.useGetAdminFcInternalSender(
				payload,
			),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/fc/admin/fc-senders/{fc_sender_id}",
				{
					params: { path: payload },
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
	});
};

export const useGetAdminFcInternalSenderHistory: CustomQueryHook<
	getAdminFcInternalSenderHistoryOutput,
	getAdminFcInternalSenderHistoryInput
> = (payload, options) => {
	return useQuery({
		...options,
		queryKey:
			ADMIN_FC_INTERNAL_SENDERS_SERVICE_KEYS.useGetAdminFcInternalSenderHistory(
				payload,
			),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/fc/admin/fc-senders/{fc_sender_id}/history/",
				{
					params: { path: payload },
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
	});
};

export const useAdminFcInternalSenderRelogin: CustomMutationHook<
	adminFcInternalSenderReloginInput,
	void
> = () => {
	const { toastApiError } = useApiError();

	return useMutation<void, ApiError, adminFcInternalSenderReloginInput>({
		mutationKey:
			ADMIN_FC_INTERNAL_SENDERS_SERVICE_KEYS.useAdminFcInternalSenderRelogin(),
		mutationFn: async (payload) => {
			const { error } = await fetchClient.POST(
				"/v1/fc/admin/fc-senders/{fc_sender_id}/relogin/",
				{
					params: { path: payload },
					throwOnError: true,
				},
			);
			if (error) throw error;
		},
		onError: (error) => toastApiError(error),
	});
};

export const useAdminFcInternalSenderStopResume: CustomMutationHook<
	adminFcInternalSenderStopResumeInput,
	void
> = () => {
	const { toastApiError } = useApiError();

	return useMutation<void, ApiError, adminFcInternalSenderStopResumeInput>({
		mutationKey:
			ADMIN_FC_INTERNAL_SENDERS_SERVICE_KEYS.useAdminFcInternalSenderStopResume(),
		mutationFn: async ({ fc_sender_id, ...body }) => {
			const { error } = await fetchClient.POST(
				"/v1/fc/admin/fc-senders/{fc_sender_id}/stop-resume/",
				{
					params: { path: { fc_sender_id } },
					body,
					throwOnError: true,
				},
			);
			if (error) throw error;
		},
		onError: (error) => toastApiError(error),
	});
};

export const useDeleteAdminFcInternalSender: CustomMutationHook<
	deleteAdminFcInternalSenderInput,
	void
> = () => {
	const { toastApiError } = useApiError();

	return useMutation<void, ApiError, deleteAdminFcInternalSenderInput>({
		mutationKey:
			ADMIN_FC_INTERNAL_SENDERS_SERVICE_KEYS.useDeleteAdminFcInternalSender(),
		mutationFn: async (payload) => {
			const { error } = await fetchClient.DELETE(
				"/v1/fc/admin/fc-senders/{fc_sender_id}",
				{
					params: { path: payload },
					throwOnError: true,
				},
			);
			if (error) throw error;
		},
		onError: (error) => toastApiError(error),
	});
};
